import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchPointsInterets } from "../../../store/actions/pointInteretActions"
import { getBasePointInteret } from "../../../utilities/defaultObjects"
import {
	POINT_INTERET_TYPE_PARC,
	POINT_INTERET_TYPE_FOURRIERE,
	POINT_INTERET_TYPE_POLICE,
	POINT_INTERET_TYPE_ATELIER,
} from "../../others/PointInteretComponent"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import PointInteretModalV2, { POI_TYPE_OPTIONS } from "./PointInteretModalV2"
import { CoordonneesResume } from "./CoordonneesPointV2"

const TYPE_LABEL = {
	[POINT_INTERET_TYPE_PARC]: "Parc",
	[POINT_INTERET_TYPE_FOURRIERE]: "Fourrière",
	[POINT_INTERET_TYPE_POLICE]: "Parc police",
	[POINT_INTERET_TYPE_ATELIER]: "Atelier",
}

const styles = {
	toolbar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 16,
		marginBottom: 18,
		flexWrap: "wrap",
	},
	left: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
	count: { fontSize: 13, color: COLORS.textLight },
	typeSelect: {
		padding: "9px 12px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		fontSize: 13,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		fontFamily: "inherit",
		outline: "none",
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	typeChip: {
		display: "inline-flex",
		alignItems: "center",
		padding: "2px 9px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
}

const formatAdresse = (adr) => {
	if (!adr) return "—"
	const ligne1 = [adr.rue, adr.numero].filter(Boolean).join(" ")
	const ligne2 = [adr.code_postal, adr.localite].filter(Boolean).join(" ")
	const full = [ligne1, ligne2].filter(Boolean).join(", ")
	return full || "—"
}

const PointRow = ({ point, index, onOpen }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onOpen(point)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={{ ...tableStyles.td, fontWeight: 500 }}>
				{point.label || "—"}
			</td>
			<td style={tableStyles.td}>
				<span style={styles.typeChip}>
					{TYPE_LABEL[point.type] || "—"}
				</span>
			</td>
			<td style={{ ...tableStyles.td, color: COLORS.textMuted }}>
				{formatAdresse(point.adresse)}
			</td>
			<td style={tableStyles.td}>
				<CoordonneesResume adresse={point.adresse} />
			</td>
		</tr>
	)
}

// Onglet "Points d'intérêt" de la Configuration : table unique (parcs +
// fourrières + parcs police + ateliers) avec filtre par catégorie + édition
// par modal.
const PointsInteretV2 = () => {
	const dispatch = useDispatch()
	const {
		parcs,
		fourrieres,
		polices,
		ateliers,
		fetchPointsInterets: fetchState,
	} = useSelector((s) => s.pointInteretInfo)
	const [typeFilter, setTypeFilter] = useState("all")
	const [editing, setEditing] = useState(null) // point | null

	useEffect(() => {
		dispatch(fetchPointsInterets())
	}, [dispatch])

	const all = [
		...(parcs || []),
		...(fourrieres || []),
		...(polices || []),
		...(ateliers || []),
	]
	const filtered = (
		typeFilter === "all"
			? all
			: all.filter((p) => p.type === typeFilter)
	).sort((a, b) => (a.label || "").localeCompare(b.label || "", "fr"))

	const loading = fetchState.loading && all.length === 0

	// "+ Nouveau" : type par défaut = le filtre courant, sinon Parc.
	const createDefaultType =
		typeFilter === "all" ? POINT_INTERET_TYPE_PARC : typeFilter

	return (
		<>
			{editing && (
				<PointInteretModalV2
					point={editing}
					onClose={() => setEditing(null)}
				/>
			)}
			<div style={styles.toolbar}>
				<div style={styles.left}>
					<select
						style={styles.typeSelect}
						value={typeFilter}
						onChange={(e) =>
							setTypeFilter(
								e.target.value === "all"
									? "all"
									: parseInt(e.target.value),
							)
						}
					>
						<option value="all">Toutes les catégories</option>
						{POI_TYPE_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
					<span style={styles.count}>{filtered.length}</span>
				</div>
				<button
					style={gs.btnPrimary}
					onClick={() =>
						setEditing(getBasePointInteret(createDefaultType))
					}
				>
					+ Nouveau point
				</button>
			</div>

			<div style={tableStyles.container}>
				{loading ? (
					<Loader variant="ring" message="Chargement…" />
				) : filtered.length === 0 ? (
					<div style={styles.empty}>Aucun point d'intérêt.</div>
				) : (
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Nom</th>
								<th style={tableStyles.th}>Catégorie</th>
								<th style={tableStyles.th}>Adresse</th>
								<th style={tableStyles.th}>Coordonnées</th>
							</tr>
						</thead>
						<tbody>
							{filtered.map((point, index) => (
								<PointRow
									key={point.id}
									point={point}
									index={index}
									onOpen={setEditing}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>
		</>
	)
}

export default PointsInteretV2
